import { create } from 'zustand';
import {
  INDUSTRY_OPTIONS,
  TENANT_PACKAGE_DESCRIPTIONS,
  TENANT_PACKAGE_LABELS,
  type TenantPackageType,
} from '@/lib/industry-packages';

/* ── Step Definitions ─────────────────────────────────────── */

export const WIZARD_STEPS = [
  { id: 'account', label: 'Account', description: 'Create your admin account' },
  { id: 'organization', label: 'Organization', description: 'Tell us about your company' },
  { id: 'plan', label: 'Plan', description: 'Choose your subscription' },
  { id: 'branding', label: 'Branding', description: 'Customize your platform' },
  { id: 'services', label: 'Services', description: 'Configure features' },
  { id: 'review', label: 'Launch', description: 'Review and go live' },
] as const;

/* ── Types ────────────────────────────────────────────────── */

export interface AccountData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  password: string;
  confirmPassword: string;
}

export interface OrganizationData {
  name: string;
  industry: string;
  tenantPackage: TenantPackageType;
  country: string;
  region: string;
  fleetSize: string;
  website: string;
}

export interface PlanData {
  tier: string;
  billingCycle: 'monthly' | 'quarterly' | 'biannual' | 'annual';
  vehicleCount: number;
  deviceCount: number;
  seatCount: number;
}

export interface BrandingData {
  logoFile: File | null;
  logoPreview: string;
  primaryColor: string;
  tagline: string;
  timezone: string;
}

export interface ServicesData {
  enabledServices: string[];
}

export interface SignupWizardState {
  currentStep: number;
  account: AccountData;
  organization: OrganizationData;
  plan: PlanData;
  branding: BrandingData;
  services: ServicesData;
  agreedToTerms: boolean;
  isSubmitting: boolean;
  submitError: string | null;
  completedSteps: number[];

  setStep: (step: number) => void;
  nextStep: () => void;
  prevStep: () => void;
  updateAccount: (data: Partial<AccountData>) => void;
  updateOrganization: (data: Partial<OrganizationData>) => void;
  updatePlan: (data: Partial<PlanData>) => void;
  updateBranding: (data: Partial<BrandingData>) => void;
  updateServices: (data: Partial<ServicesData>) => void;
  setAgreedToTerms: (agreed: boolean) => void;
  setSubmitting: (submitting: boolean) => void;
  setSubmitError: (error: string | null) => void;
  markStepComplete: (step: number) => void;
  reset: () => void;
}

/* ── Default Values ───────────────────────────────────────── */

const defaultAccount: AccountData = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  password: '',
  confirmPassword: '',
};

const defaultOrganization: OrganizationData = {
  name: '',
  industry: '',
  tenantPackage: 'core',
  country: '',
  region: '',
  fleetSize: '',
  website: '',
};

const defaultPlan: PlanData = {
  tier: 'ai',
  billingCycle: 'monthly',
  vehicleCount: 10,
  deviceCount: 10,
  seatCount: 3,
};

const defaultBranding: BrandingData = {
  logoFile: null,
  logoPreview: '',
  primaryColor: '#10b981',
  tagline: '',
  timezone: 'Africa/Johannesburg',
};

const defaultServices: ServicesData = {
  enabledServices: [
    'map', 'vehicles', 'devices', 'alerts', 'fleet',
    'users', 'cameras', 'triggers', 'settings', 'reports',
  ],
};

/* ── Store ────────────────────────────────────────────────── */

export const useSignupWizard = create<SignupWizardState>((set) => ({
  currentStep: 0,
  account: { ...defaultAccount },
  organization: { ...defaultOrganization },
  plan: { ...defaultPlan },
  branding: { ...defaultBranding },
  services: { ...defaultServices },
  agreedToTerms: false,
  isSubmitting: false,
  submitError: null,
  completedSteps: [],

  setStep: (step) => set({ currentStep: step }),
  nextStep: () =>
    set((s) => {
      const next = Math.min(s.currentStep + 1, WIZARD_STEPS.length - 1);
      const completed = s.completedSteps.includes(s.currentStep)
        ? s.completedSteps
        : [...s.completedSteps, s.currentStep];
      return { currentStep: next, completedSteps: completed };
    }),
  prevStep: () =>
    set((s) => ({ currentStep: Math.max(s.currentStep - 1, 0) })),
  updateAccount: (data) =>
    set((s) => ({ account: { ...s.account, ...data } })),
  updateOrganization: (data) =>
    set((s) => ({ organization: { ...s.organization, ...data } })),
  updatePlan: (data) =>
    set((s) => ({ plan: { ...s.plan, ...data } })),
  updateBranding: (data) =>
    set((s) => ({ branding: { ...s.branding, ...data } })),
  updateServices: (data) =>
    set((s) => ({ services: { ...s.services, ...data } })),
  setAgreedToTerms: (agreed) => set({ agreedToTerms: agreed }),
  setSubmitting: (isSubmitting) => set({ isSubmitting }),
  setSubmitError: (submitError) => set({ submitError }),
  markStepComplete: (step) =>
    set((s) => ({
      completedSteps: s.completedSteps.includes(step)
        ? s.completedSteps
        : [...s.completedSteps, step],
    })),
  reset: () =>
    set({
      currentStep: 0,
      account: { ...defaultAccount },
      organization: { ...defaultOrganization },
      plan: { ...defaultPlan },
      branding: { ...defaultBranding },
      services: { ...defaultServices },
      agreedToTerms: false,
      isSubmitting: false,
      submitError: null,
      completedSteps: [],
    }),
}));

/* ── Helpers ──────────────────────────────────────────────── */

export const industries = INDUSTRY_OPTIONS.map((industry) => ({
  ...industry,
  label:
    industry.packageType === 'insurance'
      ? `${industry.label} (Insurance package)`
      : industry.packageType === 'farming'
        ? `${industry.label} (Farming package)`
        : industry.label,
}));

export const tenantPackages = [
  {
    value: 'core',
    label: TENANT_PACKAGE_LABELS.core,
    description: TENANT_PACKAGE_DESCRIPTIONS.core,
  },
  {
    value: 'insurance',
    label: TENANT_PACKAGE_LABELS.insurance,
    description: TENANT_PACKAGE_DESCRIPTIONS.insurance,
  },
  {
    value: 'farming',
    label: TENANT_PACKAGE_LABELS.farming,
    description: TENANT_PACKAGE_DESCRIPTIONS.farming,
  },
] as const;

export const fleetSizes = [
  { value: '1-10', label: '1 - 10 vehicles' },
  { value: '11-50', label: '11 - 50 vehicles' },
  { value: '51-200', label: '51 - 200 vehicles' },
  { value: '201-500', label: '201 - 500 vehicles' },
  { value: '500+', label: '500+ vehicles' },
];

export const countries = [
  { value: 'ZA', label: 'South Africa', flag: '' },
  { value: 'NG', label: 'Nigeria', flag: '' },
  { value: 'KE', label: 'Kenya', flag: '' },
  { value: 'GH', label: 'Ghana', flag: '' },
  { value: 'EG', label: 'Egypt', flag: '' },
  { value: 'AE', label: 'UAE', flag: '' },
  { value: 'SA', label: 'Saudi Arabia', flag: '' },
  { value: 'GB', label: 'United Kingdom', flag: '' },
  { value: 'DE', label: 'Germany', flag: '' },
  { value: 'FR', label: 'France', flag: '' },
  { value: 'NL', label: 'Netherlands', flag: '' },
  { value: 'IN', label: 'India', flag: '' },
  { value: 'US', label: 'United States', flag: '' },
  { value: 'AU', label: 'Australia', flag: '' },
  { value: 'BR', label: 'Brazil', flag: '' },
  { value: 'OTHER', label: 'Other', flag: '' },
];

export const colorPresets = [
  '#10b981', '#0ea5e9', '#8b5cf6', '#f59e0b',
  '#ef4444', '#ec4899', '#06b6d4', '#14b8a6',
  '#6366f1', '#f97316', '#84cc16', '#64748b',
];

export const serviceModules = [
  { id: 'map', label: 'Live Map & Tracking', description: 'Real-time GPS tracking and map views', requiredTier: 'basic' as const },
  { id: 'vehicles', label: 'Vehicle Management', description: 'Vehicle profiles, trips, and history', requiredTier: 'basic' as const },
  { id: 'fleet', label: 'Fleet Management', description: 'Fleet grouping and dispatch', requiredTier: 'basic' as const },
  { id: 'devices', label: 'Device Hub', description: 'Device onboarding and remote config', requiredTier: 'basic' as const },
  { id: 'alerts', label: 'Alerts & Triggers', description: 'Configurable event rules and alerts', requiredTier: 'basic' as const },
  { id: 'reports', label: 'Reports & Analytics', description: 'Automated reports and dashboards', requiredTier: 'basic' as const },
  { id: 'billing', label: 'Billing Console', description: 'Billing controls, invoices, and settlements', requiredTier: 'basic' as const },
  { id: 'users', label: 'User Management', description: 'Roles, permissions, and access control', requiredTier: 'basic' as const },
  { id: 'cameras', label: 'Camera Intelligence', description: 'Live video streaming and ADAS/DMS', requiredTier: 'safety' as const },
  { id: 'triggers', label: 'Advanced Triggers', description: '100+ trigger rules with multi-channel delivery', requiredTier: 'safety' as const },
  { id: 'settings', label: 'Platform Settings', description: 'Organization and system configuration', requiredTier: 'basic' as const },
  { id: 'ai', label: 'AI Copilot', description: 'Anomaly detection and predictive insights', requiredTier: 'ai' as const },
  { id: 'ubi', label: 'UBI Risk Scoring', description: 'Usage-based insurance risk feeds', requiredTier: 'basic' as const, industryPackages: ['insurance'] as const },
  { id: 'insurance-telemetry', label: 'Insurance Telemetry', description: 'Insurer telemetry and policy operations', requiredTier: 'basic' as const, industryPackages: ['insurance'] as const },
  { id: 'insurance-crash-claims', label: 'Crash & Claims Automation', description: 'Crash event evidence and claims workflows', requiredTier: 'basic' as const, industryPackages: ['insurance'] as const },
  { id: 'insurance-risk-portfolio', label: 'Risk Portfolio Console', description: 'Portfolio-level risk analytics and alerts', requiredTier: 'basic' as const, industryPackages: ['insurance'] as const },
  { id: 'insurance-underwriting', label: 'Underwriting Workbench', description: 'Behavior-driven underwriting controls', requiredTier: 'basic' as const, industryPackages: ['insurance'] as const },
  { id: 'insurance-api-gateway', label: 'Insurance API Gateway', description: 'Policy and insurer integration channels', requiredTier: 'basic' as const, industryPackages: ['insurance'] as const },
  { id: 'insurance-fraud-compliance', label: 'Fraud & Compliance', description: 'Fraud analytics and governance guardrails', requiredTier: 'basic' as const, industryPackages: ['insurance'] as const },
  { id: 'insurance-leaderboard-incentive', label: 'Leaderboard & Incentives', description: 'Behavior leaderboard and reward automation', requiredTier: 'basic' as const, industryPackages: ['insurance'] as const },
  { id: 'farmersTelemetry', label: 'Farmers Telemetry', description: 'Livestock, land, and farm work operations', requiredTier: 'basic' as const, industryPackages: ['farming'] as const },
];

const tierLevel: Record<string, number> = {
  basic: 0,
  safety: 1,
  ai: 2,
  enterprise: 3,
  custom: 4,
};

export function isServiceAvailable(serviceRequiredTier: string, selectedTier: string): boolean {
  return (tierLevel[selectedTier] ?? 0) >= (tierLevel[serviceRequiredTier] ?? 0);
}

export function getSlugFromName(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 40);
}
